import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { Outlet } from 'react-router-dom-v5-compat'

import { Temporal } from '@js-temporal/polyfill'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Button,
  EmptyState,
  Flex,
  Heading,
  IconButton,
  InfoIcon,
  MessageCircleHeartIcon,
  ScrollArea,
  SearchIcon,
  Sidebar,
  Skeleton,
  Text,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { PageLayout } from '~publish/components/PageLayout'
import { usePageTitle } from '~publish/hooks/usePageTitle'

import {
  isCreatorCampTemplate,
  useFilteredTemplates,
} from '../../hooks/useFilteredTemplates'
import { useTemplates } from '../../hooks/useTemplates'
import type { Template } from '../../notion-sync/interface'
import { CreatorCampInternalCalendar } from '../CreatorCamp/CreatorCampInternalCalendar'
import { CreatorCampIntroBanner } from '../CreatorCamp/CreatorCampIntroBanner'
import { DailyPromptBanner } from '../CreatorCamp/DailyPromptBanner'
import { FilterByTemplateType, MoreFilters } from '../Filters/Filters'
import {
  TemplateMiniCard,
  TemplateMiniCardLink,
} from '../Template/TemplateMiniCard'
import { ToggleTip } from '../ToggleTip'

import styles from './TemplateLibraryPage.module.css'

// TODO: creator-camp-cleanup
export type CreatorCampTemplate = Template & {
  creatorCampDate: string
}

/**
 * Calculate Day number for Creator Camp templates based on the creatorCampDate
 * relative to the start date of the camp on 2025-06-16. The camp runs for 30 days
 * from the start date and has no templates for weekends, so the final template number
 * should be the duration minus the amount of weekend days.
 */
// TODO: creator-camp-cleanup
const calculateTemplateCreatorCampDayNumber = (
  creatorCampTemplateDate: string,
): string => {
  const startDate = new Date('2025-06-16')
  const duration =
    new Date(creatorCampTemplateDate).getTime() - startDate.getTime()
  const durationInDays = Math.floor(duration / (1000 * 60 * 60 * 24))
  return `${durationInDays + 1}`
}

// TODO: creator-camp-cleanup
const getCreatorCampTagForTemplate = (
  creatorCampTemplateDate: string | null,
  creatorCampFilterEnabled: boolean,
  todayDate: string,
): string | undefined => {
  if (!creatorCampTemplateDate) return undefined
  if (creatorCampFilterEnabled) {
    return `Day ${calculateTemplateCreatorCampDayNumber(
      creatorCampTemplateDate,
    )}`
  }
  if (new Date(creatorCampTemplateDate) <= new Date(todayDate)) {
    return 'Creator Camp'
  }
}

const NoTemplatesFound = ({
  clearFilters,
}: {
  clearFilters: () => void
}): JSX.Element => {
  return (
    <EmptyState size="medium" className={styles.emptyState}>
      <EmptyState.Icon>
        <SearchIcon />
      </EmptyState.Icon>
      <EmptyState.Heading size="small">No templates found</EmptyState.Heading>
      <EmptyState.Description>
        Try adjusting your filters or try a different search.
      </EmptyState.Description>
      <Button onClick={clearFilters}>Clear filters</Button>
    </EmptyState>
  )
}

export const TemplateLibraryPage = (): JSX.Element => {
  usePageTitle('Templates')

  // TODO: creator-camp-cleanup
  const creatorCampBannerEnabled = useIsSplitEnabled(
    'creator_camp_banner_june_2025',
  )
  // TODO: creator-camp-cleanup
  const creatorCampDailyPromptSplitEnabled = useIsSplitEnabled(
    'creator_camp_daily_prompt_banner',
  )
  // TODO: creator-camp-cleanup
  const creatorCampInternalDateSelectorEnabled = useIsSplitEnabled(
    'creator_camp_internal_date_selector',
  )

  // TODO: creator-camp-cleanup
  const [creatorCampTodayDate, setCreatorCampTodayDate] = React.useState<
    Temporal.PlainDate | undefined
  >()
  // TODO: creator-camp-cleanup
  const actualTodayDate = Temporal.Now.plainDateISO()
  const todayDate = creatorCampTodayDate ?? actualTodayDate

  // TODO: creator-camp-cleanup
  const creatorCampDailyPromptBannerEnabled =
    !!todayDate || creatorCampDailyPromptSplitEnabled

  const isMobileScreenSize = useIsMobileScreenSize()
  const { templates, loading } = useTemplates()

  const [templateTypeFilter, setTemplateTypeFilter] =
    React.useState<string>('All')
  const [templateFilters, setTemplateFilters] = React.useState<string[]>([])

  // TODO: creator-camp-cleanup
  const handleCreatorCampTodayDateChange = (
    date: Temporal.PlainDate | undefined,
  ): void => {
    if (date) {
      setTemplateTypeFilter('Creator Camp')
    } else {
      setTemplateTypeFilter('All')
    }
    setCreatorCampTodayDate(date)
  }

  const resetFilters = (): void => {
    setTemplateFilters([])
    setTemplateTypeFilter('All')
  }

  const filteredTemplates = useFilteredTemplates({
    templates,
    templateTypeFilter,
    templateFilters,
    // TODO: creator-camp-cleanup
    todayDate: todayDate.toString(),
  })

  /**
   * Find today's template for the Creator Camp daily prompt banner
   */
  // TODO: creator-camp-cleanup
  const todaysTemplate = useMemo(() => {
    return (
      templates
        .filter(isCreatorCampTemplate)
        .find(
          (template) =>
            new Date(template.creatorCampDate).toDateString() ===
            new Date(todayDate.toString()).toDateString(),
        ) ?? null
    )
  }, [templates, todayDate])

  // Show border on header when scrolling
  const [showBorder, setShowBorder] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)
  useEffect(() => {
    const ref = contentRef.current
    if (!ref) return
    const handleScroll = (): void => {
      setShowBorder(ref.scrollTop > 0)
    }
    ref.addEventListener('scroll', handleScroll)
    return () => {
      ref?.removeEventListener('scroll', handleScroll)
    }
  }, [contentRef])

  if (loading)
    return (
      <PageLayout ref={contentRef}>
        <PageLayout.Header separator={showBorder}>
          <PageLayout.HeaderRow>
            <Flex align="center" gap="sm">
              {isMobileScreenSize && (
                <Sidebar.Trigger aria-label="Toggle sidebar" />
              )}
              <Heading size="large" truncate>
                Templates
              </Heading>
            </Flex>
          </PageLayout.HeaderRow>
        </PageLayout.Header>
        <PageLayout.Container className={styles.feedContent}>
          <div className={styles.templateGrid}>
            {Array.from({ length: 24 }).map((_, idx) => (
              <Skeleton key={idx} height={120} />
            ))}
          </div>
        </PageLayout.Container>
      </PageLayout>
    )

  return (
    <>
      <Outlet />
      <PageLayout>
        <PageLayout.Header
          separator={false}
          className={styles.templatePageHeader}
        >
          <PageLayout.HeaderRow>
            <Flex align="center" gap="sm">
              {isMobileScreenSize && (
                <Sidebar.Trigger aria-label="Toggle sidebar" />
              )}
              <Heading size="large" truncate>
                Templates
              </Heading>
              <ToggleTip
                content={
                  <Text>
                    Templates make posting easy. Just pick one
                    <br /> you like, customize it, and share.
                    <br />
                    New templates are added every week ✨
                  </Text>
                }
              >
                <IconButton label="Templates" variant="tertiary" size="medium">
                  <InfoIcon />
                </IconButton>
              </ToggleTip>
              {/* TODO: creator-camp-cleanup */}
              {creatorCampInternalDateSelectorEnabled && (
                <CreatorCampInternalCalendar
                  todayDate={todayDate}
                  setCreatorCampTodayDate={handleCreatorCampTodayDateChange}
                />
              )}
            </Flex>
            <FeedbackWidget id="template-library" source="create">
              <Button variant="tertiary" size="medium">
                <MessageCircleHeartIcon aria-hidden="true" />
                Share Feedback
              </Button>
            </FeedbackWidget>
          </PageLayout.HeaderRow>
          {/* TODO: creator-camp-cleanup */}
          {creatorCampBannerEnabled && !creatorCampDailyPromptBannerEnabled && (
            <PageLayout.HeaderRow>
              <CreatorCampIntroBanner />
            </PageLayout.HeaderRow>
          )}
          {/* TODO: creator-camp-cleanup */}
          {creatorCampDailyPromptBannerEnabled && todaysTemplate && (
            <PageLayout.HeaderRow>
              <DailyPromptBanner
                template={todaysTemplate}
                todayDate={todayDate.toString()}
              />
            </PageLayout.HeaderRow>
          )}

          <PageLayout.HeaderRow>
            <PageLayout.Actions className={styles.headerActions}>
              <ScrollArea
                scrollbars="horizontal"
                visibility="none"
                gradientFade={40}
              >
                <Flex align="center" className={styles.chipsFilterGroup}>
                  <FilterByTemplateType
                    value={templateTypeFilter}
                    onChange={setTemplateTypeFilter}
                    // TODO: creator-camp-cleanup
                    isCreatorCampActive={creatorCampDailyPromptBannerEnabled}
                  />
                </Flex>
              </ScrollArea>
              <MoreFilters
                value={templateFilters}
                onChange={setTemplateFilters}
                onReset={resetFilters}
              />
            </PageLayout.Actions>
          </PageLayout.HeaderRow>
        </PageLayout.Header>
        <PageLayout.Container className={styles.feedContent}>
          {filteredTemplates.length > 0 ? (
            <div className={styles.templateGrid}>
              {filteredTemplates.map((template) => (
                <TemplateMiniCardLink
                  key={template.id}
                  templateId={template.id}
                  cta="create-templates-browse-list-1"
                >
                  <TemplateMiniCard
                    template={template}
                    // TODO: creator-camp-cleanup
                    creatorCampTag={getCreatorCampTagForTemplate(
                      template.creatorCampDate,
                      templateTypeFilter === 'Creator Camp',
                      todayDate.toString(),
                    )}
                  />
                </TemplateMiniCardLink>
              ))}
              {/* This bit of logic adds empty grid items to fill the grid and avoid
            stretching items too much when there are only 1-3 items. This was easier
            than wrangling with CSS grid's auto-fit behavior. -HSM */}
              {Array.from({
                length: Math.max(0, 4 - filteredTemplates.length),
              }).map((_, idx) => (
                <div key={`empty-${idx}`} className={styles.emptyGridItem} />
              ))}
            </div>
          ) : (
            <NoTemplatesFound clearFilters={resetFilters} />
          )}
        </PageLayout.Container>
      </PageLayout>
    </>
  )
}
